/**
 * Experience Settings Panel - THE ONESCENT DIAL
 * Animation, Sound, Haptic, and Zoom Controls with Quick Toggle + Detailed Settings
 */

import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  X, Volume2, VolumeX, Sparkles, Settings, 
  Waves, Zap, Music, Play, Pause, RotateCcw, Vibrate,
  ZoomIn, ZoomOut, Maximize, Download, Smartphone, Monitor
} from 'lucide-react';
import { toast } from './ui/sonner';
import { useExperience } from '../contexts/ExperienceContext';
import { usePWAInstall } from '../hooks/usePWAInstall';
import { modalVariants, backdropVariants } from '../utils/animationConfig';
import { useResponsive } from '../hooks/useResponsive';

// =============================================================================
// QUICK TOGGLE (Floating Corner Button)
// =============================================================================

export const ExperienceQuickToggle = () => {
  const { 
    animationPrefs, 
    soundPrefs, 
    toggleAnimations, 
    toggleSound,
    setShowSettings,
    initializeAudio,
    playSound,
    hapticEnabled,
    toggleHaptic,
    hapticSupported,
    triggerHaptic,
    zoomLevel,
    handleZoomIn,
    handleZoomOut,
    canZoomIn,
    canZoomOut,
  } = useExperience();
  
  // Get screen dimensions to hide controls on small screens
  const responsive = useResponsive();
  // Hide individual toggles (animation, sound, haptics) when screen height is too short
  // This prevents overlap with main navigation buttons on the right side menu
  // Individual toggles only show when there's enough vertical space (height > 650px)
  const showIndividualToggles = responsive.height > 650;
  // Hide zoom controls on very short screens to prevent overlap with main menu
  // Users can still access zoom from the settings panel
  const showZoomControls = responsive.height > 650;
  
  const handleSoundToggle = async () => {
    await initializeAudio();
    toggleSound();
    // Show feedback toast
    const newEnabled = !soundPrefs.enabled;
    toast.success(newEnabled ? 'Sound enabled' : 'Sound disabled', { duration: 1500 });
    // Play click sound after toggle (new state will have sound enabled if it was off)
    if (!soundPrefs.enabled) {
      setTimeout(() => playSound('click'), 100);
    }
  };
  
  const handleOpenSettings = (e) => {
    e.stopPropagation();
    e.preventDefault();
    triggerHaptic('light');
    playSound('click');
    setShowSettings(true);
  };
  
  const handleAnimationToggle = (e) => {
    e.stopPropagation();
    toggleAnimations();
    playSound('click');
    // Show feedback toast
    const newEnabled = !animationPrefs.enabled;
    toast.success(newEnabled ? 'Animations enabled' : 'Animations disabled', { duration: 1500 });
  };
  
  const handleHapticToggle = (e) => {
    e.stopPropagation();
    toggleHaptic();
    playSound('click');
    // Show feedback toast
    const newEnabled = !hapticEnabled;
    toast.success(newEnabled ? 'Haptic feedback enabled' : 'Haptic feedback disabled', { duration: 1500 });
  };
  
  return (
    <div 
      className="fixed bottom-24 sm:bottom-20 right-3 z-[9998] flex flex-col gap-2 pointer-events-auto"
      data-testid="experience-quick-toggle"
      style={{ isolation: 'isolate' }}
    >
      {/* Zoom Controls - Hidden on short screens to prevent overlap with main menu */}
      {showZoomControls && (
        <div className="flex flex-col gap-1 bg-tod-bg-secondary/90 backdrop-blur-sm rounded-full p-1 border border-tod-brass/20">
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={(e) => { e.stopPropagation(); handleZoomIn(); playSound('click'); }}
            disabled={!canZoomIn}
            className={`p-2 rounded-full transition-colors ${
              canZoomIn 
                ? 'hover:bg-tod-brass/20 text-tod-text-secondary hover:text-tod-brass' 
                : 'opacity-30 cursor-not-allowed text-tod-text-secondary'
            }`}
            title="Zoom In"
            data-testid="zoom-in-btn"
          >
            <ZoomIn size={16} />
          </motion.button>
          
          <div className="text-[10px] text-tod-text-secondary text-center font-mono">
            {Math.round(zoomLevel * 100)}%
          </div>
          
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={(e) => { e.stopPropagation(); handleZoomOut(); playSound('click'); }}
            disabled={!canZoomOut}
            className={`p-2 rounded-full transition-colors ${
              canZoomOut 
                ? 'hover:bg-tod-brass/20 text-tod-text-secondary hover:text-tod-brass' 
                : 'opacity-30 cursor-not-allowed text-tod-text-secondary'
            }`}
            title="Zoom Out"
            data-testid="zoom-out-btn"
          >
            <ZoomOut size={16} />
          </motion.button>
        </div>
      )}
      
      {/* Individual toggles - Hidden on very small screens to prevent overlap with nav buttons */}
      {showIndividualToggles && (
        <>
          {/* Animation Toggle */}
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={handleAnimationToggle}
            className={`p-2.5 rounded-full border backdrop-blur-sm shadow-lg transition-colors ${
              animationPrefs.enabled 
                ? 'bg-tod-brass/20 border-tod-brass/40 text-tod-brass' 
                : 'bg-tod-bg-secondary/80 border-tod-brass/20 text-tod-text-secondary'
            }`}
            title={animationPrefs.enabled ? 'Disable animations' : 'Enable animations'}
          >
            <Sparkles size={18} className={animationPrefs.enabled ? 'animate-pulse' : ''} />
          </motion.button>
          
          {/* Sound Toggle */}
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={async (e) => {
              e.stopPropagation();
              await handleSoundToggle();
            }}
            className={`p-2.5 rounded-full border backdrop-blur-sm shadow-lg transition-colors ${
              soundPrefs.enabled 
                ? 'bg-tod-brass/20 border-tod-brass/40 text-tod-brass' 
                : 'bg-tod-bg-secondary/80 border-tod-brass/20 text-tod-text-secondary'
            }`}
            title={soundPrefs.enabled ? 'Disable sound' : 'Enable sound'}
          >
            {soundPrefs.enabled ? <Volume2 size={18} /> : <VolumeX size={18} />}
          </motion.button>
          
          {/* Haptic Toggle - Only show on mobile devices (not tablet/desktop) */}
          {hapticSupported && responsive.isPhone && (
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={handleHapticToggle}
              className={`p-2.5 rounded-full border backdrop-blur-sm shadow-lg transition-colors ${
                hapticEnabled 
                  ? 'bg-tod-brass/20 border-tod-brass/40 text-tod-brass' 
                  : 'bg-tod-bg-secondary/80 border-tod-brass/20 text-tod-text-secondary'
              }`}
              title={hapticEnabled ? 'Disable haptic feedback' : 'Enable haptic feedback'}
            >
              <Vibrate size={18} />
            </motion.button>
          )}
        </>
      )}
      
      {/* Settings Button - Always visible */}
      <motion.button
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        onClick={handleOpenSettings}
        className="p-2.5 rounded-full bg-tod-bg-secondary/80 border border-tod-brass/20 text-tod-text-secondary backdrop-blur-sm shadow-lg hover:border-tod-brass/40 hover:text-tod-brass transition-colors"
        title="Experience settings"
      >
        <Settings size={18} />
      </motion.button>
    </div>
  );
};

// =============================================================================
// DETAILED SETTINGS PANEL
// =============================================================================

export const ExperienceSettingsPanel = () => {
  const responsive = useResponsive();
  const {
    animationPrefs,
    updateAnimationPrefs,
    soundPrefs,
    updateSoundPrefs,
    showSettings,
    setShowSettings,
    resetToDefaults,
    playSound,
    startAmbient,
    stopAmbient,
    initializeAudio,
    hapticEnabled,
    toggleHaptic,
    hapticSupported,
    triggerHaptic,
    // Zoom controls for settings panel
    zoomLevel,
    handleZoomIn,
    handleZoomOut,
    canZoomIn,
    canZoomOut,
  } = useExperience();
  
  const [testingAmbient, setTestingAmbient] = useState(null);
  const { canInstall, isInstalled, isIOS, promptInstall } = usePWAInstall();
  
  const handleClose = () => {
    if (testingAmbient) {
      stopAmbient();
      setTestingAmbient(null);
    }
    triggerHaptic('light');
    playSound('modalClose');
    setShowSettings(false);
  };
  
  const testSound = async (soundName) => {
    await initializeAudio();
    triggerHaptic('light');
    playSound(soundName);
  };
  
  const testAmbient = async (theme) => {
    await initializeAudio();
    triggerHaptic('selection');
    if (testingAmbient === theme) {
      stopAmbient();
      setTestingAmbient(null);
    } else {
      startAmbient(theme);
      setTestingAmbient(theme);
    }
  };
  
  const testHaptic = (type) => {
    triggerHaptic(type);
  };
  
  // Play modal open sound when panel opens
  React.useEffect(() => {
    if (showSettings) {
      playSound('modalOpen');
    }
  }, [showSettings, playSound]);
  
  if (!showSettings) return null;
  
  return (
    <AnimatePresence>
      <motion.div
        key="settings-backdrop"
        variants={backdropVariants}
        initial="hidden"
        animate="visible"
        exit="exit"
        className="fixed inset-0 z-[70] flex items-start sm:items-center justify-center bg-black/70 backdrop-blur-sm p-2 sm:p-4 pt-12 sm:pt-4"
        onClick={handleClose}
        data-testid="experience-settings-backdrop"
      >
        <motion.div
          key="settings-panel"
          variants={modalVariants}
          initial="hidden"
          animate="visible"
          exit="exit"
          onClick={e => e.stopPropagation()}
          className="bg-tod-bg-primary border border-tod-brass/40 rounded-xl shadow-2xl overflow-hidden flex flex-col"
          style={{
            width: responsive.isPhone ? '95vw' : responsive.isTablet ? '380px' : '480px',
            maxWidth: '95vw',
            maxHeight: responsive.isPhone ? '85vh' : '85vh'
          }}
          data-testid="experience-settings-panel"
        >
          {/* Header */}
          <div className="flex-shrink-0 flex items-center justify-between p-3 sm:p-4 border-b border-tod-brass/20 bg-gradient-to-r from-purple-500/10 via-transparent to-tod-brass/10">
            <div className="flex items-center gap-2 sm:gap-3">
              <div className="p-1.5 sm:p-2 rounded-lg bg-gradient-to-br from-tod-brass/20 to-purple-500/20">
                <Sparkles size={18} className="text-tod-brass" />
              </div>
              <div>
                <h2 className="text-sm sm:text-base font-bold text-tod-text-primary">Experience Settings</h2>
                <p className="text-[10px] sm:text-xs text-tod-text-secondary">Animation & Sound Controls</p>
              </div>
            </div>
            <button 
              onClick={handleClose} 
              className="p-2 hover:bg-tod-brass/20 rounded-lg transition-colors"
            >
              <X size={18} className="text-tod-text-secondary" />
            </button>
          </div>
          
          {/* Content */}
          <div className="flex-1 overflow-y-auto p-4 space-y-6">
            
            {/* Zoom Controls Section */}
            <section>
              <div className="flex items-center gap-2 mb-3">
                <Maximize size={16} className="text-tod-brass" />
                <h3 className="text-sm font-semibold text-tod-text-primary">Zoom</h3>
              </div>
              
              <div className="p-3 bg-tod-bg-secondary rounded-lg border border-tod-brass/20">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-tod-text-primary">Dial Zoom Level</span>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => { handleZoomOut(); playSound('click'); }}
                      disabled={!canZoomOut}
                      className={`p-2 rounded-lg transition-colors ${
                        canZoomOut 
                          ? 'bg-tod-bg-primary border border-tod-brass/30 text-tod-text-secondary hover:border-tod-brass/50 hover:text-tod-brass' 
                          : 'opacity-30 cursor-not-allowed bg-tod-bg-primary border border-tod-brass/20 text-tod-text-secondary'
                      }`}
                      title="Zoom Out"
                      data-testid="settings-zoom-out-btn"
                    >
                      <ZoomOut size={16} />
                    </button>
                    
                    <div className="min-w-[50px] text-center">
                      <span className="text-sm font-mono text-tod-brass">{Math.round(zoomLevel * 100)}%</span>
                    </div>
                    
                    <button
                      onClick={() => { handleZoomIn(); playSound('click'); }}
                      disabled={!canZoomIn}
                      className={`p-2 rounded-lg transition-colors ${
                        canZoomIn 
                          ? 'bg-tod-bg-primary border border-tod-brass/30 text-tod-text-secondary hover:border-tod-brass/50 hover:text-tod-brass' 
                          : 'opacity-30 cursor-not-allowed bg-tod-bg-primary border border-tod-brass/20 text-tod-text-secondary'
                      }`}
                      title="Zoom In"
                      data-testid="settings-zoom-in-btn"
                    >
                      <ZoomIn size={16} />
                    </button>
                  </div>
                </div>
                <p className="text-xs text-tod-text-secondary mt-2">
                  Adjust the dial size for better visibility
                </p>
              </div>
            </section>
            
            {/* Animation Settings */}
            <section>
              <div className="flex items-center gap-2 mb-3">
                <Zap size={16} className="text-tod-brass" />
                <h3 className="text-sm font-semibold text-tod-text-primary">Animations</h3>
              </div>
              
              <div className="space-y-3">
                {/* Enable/Disable */}
                <label className="flex items-center justify-between p-3 bg-tod-bg-secondary rounded-lg border border-tod-brass/20">
                  <span className="text-sm text-tod-text-primary">Enable Animations</span>
                  <button
                    onClick={() => updateAnimationPrefs({ enabled: !animationPrefs.enabled })}
                    className={`w-12 h-6 rounded-full transition-colors relative ${
                      animationPrefs.enabled ? 'bg-tod-brass' : 'bg-tod-bg-primary border border-tod-brass/30'
                    }`}
                  >
                    <div className={`absolute top-1 w-4 h-4 rounded-full bg-white shadow transition-transform ${
                      animationPrefs.enabled ? 'translate-x-7' : 'translate-x-1'
                    }`} />
                  </button>
                </label>
                
                {/* Intensity */}
                <div className="p-3 bg-tod-bg-secondary rounded-lg border border-tod-brass/20">
                  <span className="text-sm text-tod-text-primary block mb-2">Intensity</span>
                  <div className="grid grid-cols-4 gap-2">
                    {['off', 'subtle', 'normal', 'enhanced'].map(level => (
                      <button
                        key={level}
                        onClick={() => updateAnimationPrefs({ intensity: level, enabled: level !== 'off' })}
                        className={`py-2 px-3 rounded-lg text-xs font-medium transition-colors capitalize ${
                          animationPrefs.intensity === level
                            ? 'bg-tod-brass text-tod-bg-primary'
                            : 'bg-tod-bg-primary border border-tod-brass/30 text-tod-text-secondary hover:border-tod-brass/50'
                        }`}
                      >
                        {level}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </section>
            
            {/* Sound Settings */}
            <section>
              <div className="flex items-center gap-2 mb-3">
                <Volume2 size={16} className="text-purple-400" />
                <h3 className="text-sm font-semibold text-tod-text-primary">Sound</h3>
              </div>
              
              <div className="space-y-3">
                {/* Enable/Disable */}
                <label className="flex items-center justify-between p-3 bg-tod-bg-secondary rounded-lg border border-tod-brass/20">
                  <span className="text-sm text-tod-text-primary">Enable Sound</span>
                  <button
                    onClick={async () => {
                      await initializeAudio();
                      updateSoundPrefs({ enabled: !soundPrefs.enabled });
                    }}
                    className={`w-12 h-6 rounded-full transition-colors relative ${
                      soundPrefs.enabled ? 'bg-purple-500' : 'bg-tod-bg-primary border border-tod-brass/30'
                    }`}
                  >
                    <div className={`absolute top-1 w-4 h-4 rounded-full bg-white shadow transition-transform ${
                      soundPrefs.enabled ? 'translate-x-7' : 'translate-x-1'
                    }`} />
                  </button>
                </label>
                
                {soundPrefs.enabled && (
                  <>
                    {/* Master Volume */}
                    <div className="p-3 bg-tod-bg-secondary rounded-lg border border-tod-brass/20">
                      <span className="text-sm text-tod-text-primary block mb-2">Master Volume</span>
                      <div className="grid grid-cols-4 gap-2">
                        {['off', 'low', 'medium', 'high'].map(level => (
                          <button
                            key={level}
                            onClick={() => updateSoundPrefs({ masterVolume: level, enabled: level !== 'off' })}
                            className={`py-2 px-3 rounded-lg text-xs font-medium transition-colors capitalize ${
                              soundPrefs.masterVolume === level
                                ? 'bg-purple-500 text-white'
                                : 'bg-tod-bg-primary border border-tod-brass/30 text-tod-text-secondary hover:border-tod-brass/50'
                            }`}
                          >
                            {level}
                          </button>
                        ))}
                      </div>
                    </div>
                    
                    {/* Ambient Sounds */}
                    <div className="p-3 bg-tod-bg-secondary rounded-lg border border-tod-brass/20">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <Waves size={14} className="text-cyan-400" />
                          <span className="text-sm text-tod-text-primary">Ambient Soundscapes</span>
                        </div>
                        <button
                          onClick={() => updateSoundPrefs({ ambientEnabled: !soundPrefs.ambientEnabled })}
                          className={`w-10 h-5 rounded-full transition-colors relative ${
                            soundPrefs.ambientEnabled ? 'bg-cyan-500' : 'bg-tod-bg-primary border border-tod-brass/30'
                          }`}
                        >
                          <div className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform ${
                            soundPrefs.ambientEnabled ? 'translate-x-5' : 'translate-x-0.5'
                          }`} />
                        </button>
                      </div>
                      
                      {soundPrefs.ambientEnabled && (
                        <div className="grid grid-cols-3 gap-2 mt-3">
                          {[
                            { theme: 'spacio', label: 'Cosmic Drone', desc: 'Location' },
                            { theme: 'solar', label: 'Solar Wind', desc: 'Weather' },
                            { theme: 'celestial', label: 'Crystalline', desc: 'Planets' },
                          ].map(({ theme, label, desc }) => (
                            <button
                              key={theme}
                              onClick={() => testAmbient(theme)}
                              className={`p-2 rounded-lg text-center transition-colors ${
                                testingAmbient === theme
                                  ? 'bg-cyan-500/20 border border-cyan-500/50'
                                  : 'bg-tod-bg-primary border border-tod-brass/20 hover:border-tod-brass/40'
                              }`}
                            >
                              <div className="flex items-center justify-center gap-1 mb-1">
                                {testingAmbient === theme ? <Pause size={12} /> : <Play size={12} />}
                                <span className="text-xs font-medium text-tod-text-primary">{label}</span>
                              </div>
                              <span className="text-[10px] text-tod-text-secondary">{desc}</span>
                            </button>
                          ))}
                        </div>
                      )}
                      
                      {soundPrefs.ambientEnabled && (
                        <div className="mt-3">
                          <span className="text-xs text-tod-text-secondary block mb-2">Ambient Volume</span>
                          <div className="grid grid-cols-3 gap-2">
                            {['low', 'medium', 'high'].map(level => (
                              <button
                                key={level}
                                onClick={() => updateSoundPrefs({ ambientVolume: level })}
                                className={`py-1.5 px-2 rounded text-xs capitalize ${
                                  soundPrefs.ambientVolume === level
                                    ? 'bg-cyan-500/30 text-cyan-300 border border-cyan-500/50'
                                    : 'bg-tod-bg-primary border border-tod-brass/20 text-tod-text-secondary'
                                }`}
                              >
                                {level}
                              </button>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                    
                    {/* Effect Sounds */}
                    <div className="p-3 bg-tod-bg-secondary rounded-lg border border-tod-brass/20">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <Music size={14} className="text-amber-400" />
                          <span className="text-sm text-tod-text-primary">UI Sound Effects</span>
                        </div>
                        <button
                          onClick={() => updateSoundPrefs({ effectsEnabled: !soundPrefs.effectsEnabled })}
                          className={`w-10 h-5 rounded-full transition-colors relative ${
                            soundPrefs.effectsEnabled ? 'bg-amber-500' : 'bg-tod-bg-primary border border-tod-brass/30'
                          }`}
                        >
                          <div className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform ${
                            soundPrefs.effectsEnabled ? 'translate-x-5' : 'translate-x-0.5'
                          }`} />
                        </button>
                      </div>
                      
                      {soundPrefs.effectsEnabled && (
                        <>
                          <div className="grid grid-cols-4 gap-2 mt-3">
                            {[
                              { name: 'click', label: 'Click' },
                              { name: 'modalOpen', label: 'Open' },
                              { name: 'success', label: 'Success' },
                              { name: 'notification', label: 'Alert' },
                            ].map(({ name, label }) => (
                              <button
                                key={name}
                                onClick={() => testSound(name)}
                                className="p-2 bg-tod-bg-primary border border-tod-brass/20 rounded text-xs text-tod-text-secondary hover:border-amber-500/50 hover:text-amber-400 transition-colors"
                              >
                                {label}
                              </button>
                            ))}
                          </div>
                          
                          <div className="mt-3">
                            <span className="text-xs text-tod-text-secondary block mb-2">Effects Volume</span>
                            <div className="grid grid-cols-3 gap-2">
                              {['low', 'medium', 'high'].map(level => (
                                <button
                                  key={level}
                                  onClick={() => updateSoundPrefs({ effectsVolume: level })}
                                  className={`py-1.5 px-2 rounded text-xs capitalize ${
                                    soundPrefs.effectsVolume === level
                                      ? 'bg-amber-500/30 text-amber-300 border border-amber-500/50'
                                      : 'bg-tod-bg-primary border border-tod-brass/20 text-tod-text-secondary'
                                  }`}
                                >
                                  {level}
                                </button>
                              ))}
                            </div>
                          </div>
                        </>
                      )}
                    </div>
                  </>
                )}
              </div>
            </section>
            
            {/* Haptic Feedback Settings - Only on mobile devices */}
            {hapticSupported && responsive.isPhone && (
              <section>
                <div className="flex items-center gap-2 mb-3">
                  <Vibrate size={16} className="text-cyan-400" />
                  <h3 className="text-sm font-semibold text-tod-text-primary">Haptic Feedback</h3>
                  <span className="text-[10px] px-1.5 py-0.5 bg-cyan-500/20 text-cyan-400 rounded">Mobile</span>
                </div>
                
                <div className="space-y-3">
                  {/* Enable/Disable */}
                  <label className="flex items-center justify-between p-3 bg-tod-bg-secondary rounded-lg border border-tod-brass/20">
                    <div>
                      <span className="text-sm text-tod-text-primary block">Enable Haptics</span>
                      <span className="text-xs text-tod-text-secondary">Vibration feedback for actions</span>
                    </div>
                    <button
                      onClick={toggleHaptic}
                      className={`w-12 h-6 rounded-full transition-colors relative ${
                        hapticEnabled ? 'bg-cyan-500' : 'bg-tod-bg-primary border border-tod-brass/30'
                      }`}
                    >
                      <div className={`absolute top-1 w-4 h-4 rounded-full bg-white shadow transition-transform ${
                        hapticEnabled ? 'translate-x-7' : 'translate-x-1'
                      }`} />
                    </button>
                  </label>
                  
                  {hapticEnabled && (
                    <div className="p-3 bg-tod-bg-secondary rounded-lg border border-tod-brass/20">
                      <span className="text-xs text-tod-text-secondary block mb-2">Test Haptic Patterns</span>
                      <div className="grid grid-cols-4 gap-2">
                        {[
                          { type: 'light', label: 'Light' },
                          { type: 'medium', label: 'Medium' },
                          { type: 'double', label: 'Double' },
                          { type: 'pulse', label: 'Pulse' },
                        ].map(({ type, label }) => (
                          <button
                            key={type}
                            onClick={() => testHaptic(type)}
                            className="p-2 bg-tod-bg-primary border border-tod-brass/20 rounded text-xs text-tod-text-secondary hover:border-cyan-500/50 hover:text-cyan-400 transition-colors"
                          >
                            {label}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </section>
            )}
            {/* Install App Section */}
            {!isInstalled && (
              <section>
                <div className="flex items-center gap-2 mb-3">
                  <Download size={16} className="text-tod-brass" />
                  <h3 className="text-sm font-semibold text-tod-text-primary">Install App</h3>
                </div>
                <div className="p-3 bg-tod-bg-secondary rounded-lg border border-tod-brass/20 space-y-3">
                  {canInstall ? (
                    <>
                      <p className="text-xs text-tod-text-secondary">
                        Install The Onescent Dial for a fullscreen, app-like experience with offline support.
                      </p>
                      <button
                        onClick={async () => {
                          const accepted = await promptInstall();
                          if (accepted) toast.success('App installed!');
                        }}
                        data-testid="pwa-install-settings-btn"
                        className="w-full flex items-center justify-center gap-2 py-2.5 bg-gradient-to-r from-[#E6C288] to-[#C5A059] text-[#0B0F19] rounded-lg text-sm font-semibold hover:opacity-90 transition-opacity"
                      >
                        <Download size={16} />
                        Install The Onescent Dial
                      </button>
                    </>
                  ) : isIOS ? (
                    <div className="space-y-2">
                      <p className="text-xs text-tod-text-secondary">
                        To install on your iOS device:
                      </p>
                      <div className="flex items-start gap-3 p-2 bg-tod-bg-primary rounded border border-tod-brass/10">
                        <Smartphone size={20} className="text-tod-brass flex-shrink-0 mt-0.5" />
                        <div className="text-xs text-tod-text-secondary space-y-1">
                          <p><strong className="text-tod-text-primary">1.</strong> Tap the <strong className="text-tod-text-primary">Share</strong> button in Safari</p>
                          <p><strong className="text-tod-text-primary">2.</strong> Scroll down and tap <strong className="text-tod-text-primary">Add to Home Screen</strong></p>
                          <p><strong className="text-tod-text-primary">3.</strong> Tap <strong className="text-tod-text-primary">Add</strong></p>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-center gap-3 text-xs text-tod-text-secondary">
                      <Monitor size={16} className="text-tod-brass flex-shrink-0" />
                      <p>Open in Chrome or Edge to enable the install option, or look for "Install" in your browser menu.</p>
                    </div>
                  )}
                </div>
              </section>
            )}
          </div>
          
          {/* Footer */}
          <div className="flex-shrink-0 p-4 border-t border-tod-brass/20 flex justify-between items-center">
            <button
              onClick={resetToDefaults}
              className="flex items-center gap-2 text-xs text-tod-text-secondary hover:text-tod-brass transition-colors"
            >
              <RotateCcw size={14} />
              Reset to Defaults
            </button>
            <button
              onClick={handleClose}
              className="px-4 py-2 bg-tod-brass text-tod-bg-primary rounded-lg text-sm font-medium hover:bg-tod-text-primary transition-colors"
            >
              Done
            </button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default ExperienceSettingsPanel;
